/**
 * Give Chips Section
 * Allows giving chips to all players or individual players via a popover
 */

import { View, Text, Pressable, TouchableOpacity } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useState, useCallback } from 'react'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { Popover } from '../Popover'
import { teacherControlStyles as styles } from './styles'
import type { PlayerInfo } from './types'

// Chip amounts for the give chips feature (in thousands)
const CHIP_AMOUNTS = [2, 5, 10]

type ChipRecipient = { type: 'all' } | { type: 'player'; playerId: Id<'players'>; playerName: string }

export interface GiveChipsSectionProps {
  isOpen: boolean
  onClose: () => void
  onOpen: () => void
  players: PlayerInfo[]
  onGiveChipsToAll: (amountInK: number) => void
  onGiveChipsToPlayer: (playerId: Id<'players'>, amountInK: number) => void
}

export function GiveChipsSection({
  isOpen,
  onClose,
  onOpen,
  players,
  onGiveChipsToAll,
  onGiveChipsToPlayer
}: GiveChipsSectionProps) {
  const [selectedRecipient, setSelectedRecipient] = useState<ChipRecipient | null>(null)

  const handleClose = useCallback(() => {
    setSelectedRecipient(null)
    onClose()
  }, [onClose])

  const handleBack = useCallback(() => {
    if (selectedRecipient) {
      setSelectedRecipient(null)
    } else {
      handleClose()
    }
  }, [selectedRecipient, handleClose])

  const handleSelectAmount = useCallback(
    (amountInK: number) => {
      if (!selectedRecipient) return

      if (selectedRecipient.type === 'all') {
        onGiveChipsToAll(amountInK)
      } else {
        onGiveChipsToPlayer(selectedRecipient.playerId, amountInK)
      }
      setSelectedRecipient(null)
      onClose()
    },
    [selectedRecipient, onGiveChipsToAll, onGiveChipsToPlayer, onClose]
  )

  const triggerButton = (
    <View style={[styles.button, styles.giveChipsButton]}>
      <Text style={styles.buttonText}>Give Chips</Text>
    </View>
  )

  // Content based on step
  const popoverContent = selectedRecipient ? (
    // Step 2: Amount selection
    <View>
      <View style={styles.popoverHeader}>
        <TouchableOpacity onPress={handleBack} hitSlop={8}>
          <Text style={styles.backArrow}>←</Text>
        </TouchableOpacity>
        <Text style={styles.popoverTitle}>
          {selectedRecipient.type === 'all' ? 'All Players' : selectedRecipient.playerName}
        </Text>
      </View>
      <View style={styles.chipButtonRow}>
        {CHIP_AMOUNTS.map((amount) => (
          <Pressable
            key={amount}
            onPress={() => handleSelectAmount(amount)}
            style={({ pressed }) => [styles.chipButton, pressed && { opacity: 0.7 }]}
          >
            <Text style={styles.chipButtonText}>{amount}k</Text>
          </Pressable>
        ))}
      </View>
    </View>
  ) : (
    // Step 1: Recipient selection
    <View>
      <View style={styles.popoverHeader}>
        <TouchableOpacity onPress={handleClose} hitSlop={8}>
          <Text style={styles.backArrow}>←</Text>
        </TouchableOpacity>
        <Text style={styles.popoverTitle}>Give Chips To</Text>
      </View>
      <ScrollView style={styles.recipientList} showsVerticalScrollIndicator={false}>
        {/* All Players option */}
        <Pressable
          onPress={() => setSelectedRecipient({ type: 'all' })}
          style={({ pressed }) => [styles.recipientItem, styles.recipientItemAll, pressed && { opacity: 0.7 }]}
        >
          <Text style={styles.recipientIcon}>All</Text>
          <Text style={styles.recipientName}>All Players</Text>
        </Pressable>
        {/* Individual players (sorted by chips, lowest to highest) */}
        {[...players]
          .sort((a, b) => a.chips - b.chips)
          .map((player) => (
            <Pressable
              key={player._id}
              onPress={() => setSelectedRecipient({ type: 'player', playerId: player._id, playerName: player.name })}
              style={({ pressed }) => [styles.recipientItem, pressed && { opacity: 0.7 }]}
            >
              <Text style={styles.recipientIcon}>{player.kind === 'bot' ? '🤖' : '👤'}</Text>
              <View style={styles.recipientNameRow}>
                <Text style={styles.recipientName} numberOfLines={1}>
                  {player.name}
                </Text>
                <Text style={styles.recipientChips}>{player.chips.toLocaleString()}</Text>
              </View>
            </Pressable>
          ))}
      </ScrollView>
    </View>
  )

  return (
    <Popover
      isOpen={isOpen}
      onClose={handleClose}
      onTriggerPress={onOpen}
      trigger={triggerButton}
      side="top"
      align="end"
      sideOffset={12}
      contentStyle={styles.popoverContent}
    >
      {popoverContent}
    </Popover>
  )
}
